import { useMemo } from 'react'
import { fetchCruiseOfferMemberPricing } from 'actions/CruiseActions'
import useApiQuery from 'hooks/apiQuery/useApiQuery'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanSeeLuxPlusLabel, getCanSeeLuxPlusPrice } from 'checkout/selectors/cruiseSelectors'

export type CruiseMemberPricing = {
  canSeeLuxPlusLabel: boolean
  canSeeLuxPlusPrice: boolean
  departureMemberPricing?: App.Cruises.CruiseOfferMemberPricing['departures']
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory>
}

export function useCruiseMemberPricing(
  offer?: App.CruiseOffer,
  departure?: App.CruiseDepartureDetails,
): CruiseMemberPricing {
  const hasMemberPrice = !!departure?.hasMemberPrice
  const canSeeLuxPlusPrice = useAppSelector(state => getCanSeeLuxPlusPrice(state, hasMemberPrice))
  const canSeeLuxPlusLabel = useAppSelector(state => getCanSeeLuxPlusLabel(state, hasMemberPrice))

  const offerId = offer?.id

  const query = useApiQuery({
    fieldName: 'memberPricing',
    getData: state => state.cruise.cruiseOfferMemberPricing[offerId!],
    getFetching: state => state.cruise.cruiseOfferMemberPricingLoading[offerId!],
    getError: state => state.cruise.cruiseOfferMemberPricingErrors[offerId!],
    getFetchAction: () => fetchCruiseOfferMemberPricing(offerId!),
    fetchDependencies: [offerId, canSeeLuxPlusPrice],
    enabled: !!offerId && canSeeLuxPlusPrice,
  })

  const departureMemberPricingByCategory = useMemo(() => {
    if (!departure || !canSeeLuxPlusPrice) {
      return {}
    }

    const forDeparture = query.memberPricing?.departures.find((d) => d.id === departure.id)
    return forDeparture?.lowestPricesByCategory || {}
  }, [departure, query.memberPricing, canSeeLuxPlusPrice])

  return {
    canSeeLuxPlusLabel,
    canSeeLuxPlusPrice,
    departureMemberPricing: query.memberPricing?.departures,
    departureMemberPricingByCategory,
  }
}
